import { Icon } from './ui/Icon';
import { Slider } from './ui/Slider';
import { AiChip } from './ui/AiChip';
import { useEditor, type ObjectType } from '@/store/editor';

type InspectorContent = {
  icon: string;
  title: string;
  subtitle: string;
  sections: Section[];
};

type Section =
  | { kind: 'sliders'; label: string; items: { label: string; value: number; display: string }[] }
  | { kind: 'chips'; label: string; aiLabel?: boolean; items: { icon: string; text: string }[] }
  | { kind: 'input'; label: string; value: string };

const content: Partial<Record<ObjectType, InspectorContent>> = {
  face: {
    icon: 'scan-face',
    title: 'Ansikte',
    subtitle: 'Upptackt i 4 scener',
    sections: [
      {
        kind: 'sliders',
        label: 'Justering',
        items: [
          { label: 'Ljus', value: 62, display: '+12' },
          { label: 'Skarpa', value: 50, display: '0' },
          { label: 'Hudton', value: 54, display: '+4' },
        ],
      },
      {
        kind: 'chips',
        label: 'AI foreslar',
        aiLabel: true,
        items: [
          { icon: 'sun', text: 'Battre ljussattning pa ansikte' },
          { icon: 'smile', text: 'Jamna ut hud (subtil)' },
          { icon: 'focus', text: 'Centrera i bild' },
          { icon: 'repeat', text: 'Applicera pa alla 4 scener' },
        ],
      },
    ],
  },
  sky: {
    icon: 'cloud',
    title: 'Himmel',
    subtitle: 'Soluppgang / gyllene timme',
    sections: [
      {
        kind: 'sliders',
        label: 'Atmosfar',
        items: [
          { label: 'Varme', value: 65, display: '+8' },
          { label: 'Mattnad', value: 60, display: '+5' },
          { label: 'Moln', value: 18, display: 'Fa' },
        ],
      },
      {
        kind: 'chips',
        label: 'Byt himmel till',
        aiLabel: true,
        items: [
          { icon: 'moon', text: 'Stjarnklar natthimmel' },
          { icon: 'cloud-rain', text: 'Dramatisk stormhimmel' },
          { icon: 'sun', text: 'Klarbla mitt pa dagen' },
          { icon: 'wand-2', text: 'Beskriv din egen himmel...' },
        ],
      },
    ],
  },
  sun: {
    icon: 'sun',
    title: 'Sol',
    subtitle: 'Strax ovanfor horisont',
    sections: [
      {
        kind: 'sliders',
        label: 'Ljuseffekt',
        items: [
          { label: 'Intensitet', value: 85, display: '85%' },
          { label: 'Glow', value: 70, display: 'Stor' },
          { label: 'Lens flare', value: 0, display: 'Av' },
        ],
      },
      {
        kind: 'chips',
        label: 'Snabbatgarder',
        aiLabel: true,
        items: [
          { icon: 'move', text: 'Positionera bakom person' },
          { icon: 'zap', text: 'Lagg till filmisk lens flare' },
          { icon: 'droplets', text: 'Skapa godsljus genom tradkronor' },
        ],
      },
    ],
  },
  mountain: {
    icon: 'mountain-snow',
    title: 'Berglandskap',
    subtitle: 'Silhuett i mellangrund',
    sections: [
      {
        kind: 'sliders',
        label: 'Justering',
        items: [
          { label: 'Opacitet', value: 72, display: '72%' },
          { label: 'Djup', value: 40, display: 'Medium' },
        ],
      },
      {
        kind: 'chips',
        label: 'Byt till',
        aiLabel: true,
        items: [
          { icon: 'building', text: 'Stadens silhuett' },
          { icon: 'trees', text: 'Skogsrand' },
          { icon: 'waves', text: 'Havshorisont' },
        ],
      },
    ],
  },
  person: {
    icon: 'user',
    title: 'Person',
    subtitle: 'Upptackt i 5 scener',
    sections: [
      {
        kind: 'sliders',
        label: 'Isolering',
        items: [
          { label: 'Bakgrund', value: 100, display: 'Klar' },
          { label: 'Fokus', value: 45, display: 'Medium' },
        ],
      },
      {
        kind: 'chips',
        label: 'AI foreslar',
        aiLabel: true,
        items: [
          { icon: 'aperture', text: 'Isolera, suddig bakgrund (bokeh)' },
          { icon: 'move-diagonal', text: 'Komponera enligt tredjedelsregeln' },
          { icon: 'shirt', text: 'Byt klader (beskriv)' },
          { icon: 'palette', text: 'Stilisera (cyberpunk / anime / ...)' },
        ],
      },
    ],
  },
  title: {
    icon: 'type',
    title: 'Textoverlay',
    subtitle: '"AEGIS"',
    sections: [
      { kind: 'input', label: 'Text', value: 'AEGIS' },
      {
        kind: 'sliders',
        label: 'Stil',
        items: [
          { label: 'Storlek', value: 60, display: '38' },
          { label: 'Vikt', value: 75, display: '700' },
          { label: 'Opacitet', value: 92, display: '92%' },
        ],
      },
      {
        kind: 'chips',
        label: 'Animationsforslag',
        aiLabel: true,
        items: [
          { icon: 'zap', text: 'Fade in filmiskt (2s)' },
          { icon: 'type', text: 'Typewriter-effekt' },
          { icon: 'move-up', text: 'Slide upp nerifran' },
        ],
      },
    ],
  },
};

export function Inspector() {
  const selectedObject = useEditor((s) => s.selectedObject);
  const setSelectedObject = useEditor((s) => s.setSelectedObject);

  if (selectedObject === 'default') {
    return (
      <aside className="bg-bg-panel border-l border-border overflow-y-auto w-80">
        <div className="text-center pt-10 px-6 pb-6 text-text-muted text-[13px]">
          <div className="text-text-subtle mb-3.5">
            <Icon name="mouse-pointer-click" size={32} className="mx-auto" />
          </div>
          <div className="font-medium text-text mb-1.5 text-sm">Klicka pa nagot i videon</div>
          <div>Ansikte, himmel, person, text - verktygen anpassar sig</div>
        </div>
        <div className="px-4 pb-4">
          <div className="text-[11px] uppercase tracking-wider font-semibold mb-2.5 flex items-center gap-1.5 text-ai">
            <Icon name="sparkles" size={14} />
            AI upptackte
          </div>
          <div className="flex flex-col gap-1.5">
            <AiChip icon="user" onClick={() => setSelectedObject('person')}>
              Person (centrum)
            </AiChip>
            <AiChip icon="scan-face" onClick={() => setSelectedObject('face')}>
              Ansikte
            </AiChip>
            <AiChip icon="cloud" onClick={() => setSelectedObject('sky')}>
              Himmel / soluppgang
            </AiChip>
            <AiChip icon="sun" onClick={() => setSelectedObject('sun')}>
              Sol
            </AiChip>
            <AiChip icon="mountain-snow" onClick={() => setSelectedObject('mountain')}>
              Berglandskap
            </AiChip>
            <AiChip icon="type" onClick={() => setSelectedObject('title')}>
              Text "AEGIS"
            </AiChip>
          </div>
        </div>
      </aside>
    );
  }

  const c = content[selectedObject];
  if (!c) return <aside className="bg-bg-panel border-l border-border w-80" />;

  return (
    <aside className="bg-bg-panel border-l border-border overflow-y-auto w-80 p-4">
      <div className="flex items-center gap-2.5 pb-3.5 border-b border-border mb-4">
        <div className="w-9 h-9 rounded-lg bg-[rgba(59,130,246,0.15)] text-accent flex items-center justify-center">
          <Icon name={c.icon} size={20} />
        </div>
        <div>
          <div className="font-semibold text-sm">{c.title}</div>
          <div className="text-text-muted text-xs mt-0.5">{c.subtitle}</div>
        </div>
      </div>

      {c.sections.map((section, i) => (
        <div key={i} className="mb-5">
          {section.kind === 'sliders' && (
            <>
              <SectionLabel>{section.label}</SectionLabel>
              {section.items.map((item, j) => (
                <Slider key={j} label={item.label} value={item.value} displayValue={item.display} />
              ))}
            </>
          )}
          {section.kind === 'chips' && (
            <>
              <SectionLabel ai={section.aiLabel}>
                {section.aiLabel && <Icon name="sparkles" size={14} />}
                {section.label}
              </SectionLabel>
              <div className="flex flex-col gap-1.5">
                {section.items.map((item, j) => (
                  <AiChip key={j} icon={item.icon}>
                    {item.text}
                  </AiChip>
                ))}
              </div>
            </>
          )}
          {section.kind === 'input' && (
            <>
              <SectionLabel>{section.label}</SectionLabel>
              <input
                type="text"
                defaultValue={section.value}
                className="w-full bg-bg-elevated border border-border rounded-[7px] px-3 py-2 text-sm focus:outline-none focus:border-accent"
              />
            </>
          )}
        </div>
      ))}
    </aside>
  );
}

function SectionLabel({ children, ai }: { children: React.ReactNode; ai?: boolean }) {
  return (
    <div
      className={`text-[11px] uppercase tracking-wider font-semibold mb-2.5 flex items-center gap-1.5 ${
        ai ? 'text-ai' : 'text-text-subtle'
      }`}
    >
      {children}
    </div>
  );
}
